import React, { useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { ConnectButton } from '@/components/navigation/ConnectButton';
import { useLoops } from '@/hooks/use-loops';
import LoopFeed from '@/components/loops/LoopFeed';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Hash, Users, Image, Layers, Palette, DollarSign, Sparkles, MessageCircle } from 'lucide-react';

export const HomeLanding = () => {
  const {
    user,
    profile,
    isLoading: authLoading,
    isInitialized
  } = useAuth();
  
  // Fetch loops data for preview
  const {
    data,
    isLoading: loopsLoading,
  } = useLoops('chronological');
  
  const loops = useMemo(() => 
    data?.pages.flatMap(page => page).slice(0, 3) || [], 
    [data]
  );

  // Redirect to bulletins if user is logged in
  useEffect(() => {
    if (user && profile && profile.has_completed_onboarding) {
      // Will be handled by Navigate component
    }
  }, [user, profile]);

  if (!isInitialized) {
    return <LoadingFallback />;
  }

  if (authLoading) {
    return <LoadingFallback />;
  }

  // If user exists but profile hasn't loaded yet, wait
  if (user && !profile && !authLoading) {
    return <LoadingFallback />;
  }

  // Redirect authenticated users
  if (user && profile) {
    // Primary check: has user completed onboarding?
    if (profile.has_completed_onboarding) {
      return <Navigate to="/bulletins" replace />;
    }
    
    // User has NOT completed onboarding → send to username creation
    if (!profile.has_completed_onboarding) {
      return <Navigate to="/create-username" replace />;
    }
  }

  return (
    <div className="relative w-full min-h-screen bg-[#1A1A1B] overflow-x-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-[#1A1A1B] to-[#1A1A1B] pointer-events-none z-0" />
      
      {/* Content Container - EnhancedNavigation already handles sidebar offset */}
      <div className="relative z-10 w-full min-h-screen">
        {/* Preview Feed with limited scroll */}
        <div className="w-full max-w-5xl mx-auto px-6 md:px-12 pt-12 pb-32">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
              Welcome to FOMO
            </h1>
            <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto">
              Community-driven discovery through authentic art. No bias—just transparent connections with creators who inspire you.
            </p>
          </motion.div>

          {/* Loops Preview */}
          {loopsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#272729] rounded-xl p-6 animate-pulse">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gray-700 rounded-full" />
                    <div className="flex-1">
                      <div className="h-4 bg-gray-700 rounded w-32 mb-2" />
                      <div className="h-3 bg-gray-700 rounded w-20" />
                    </div>
                  </div>
                  <div className="h-20 bg-gray-700 rounded w-full" />
                </div>
              ))}
            </div>
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="space-y-4 relative"
            >
              {loops.map((loop, index) => (
                <motion.div
                  key={loop.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                  className="pointer-events-none select-none"
                >
                  <LoopFeed 
                    loop={loop} 
                    isEngaged={false}
                    className="opacity-70 blur-[0.5px]"
                  />
                </motion.div>
              ))}
              
              {/* Fade out gradient over the feed */}
              <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#1A1A1B] via-[#1A1A1B]/80 to-transparent pointer-events-none" />
            </motion.div>
          )}
        </div>

        {/* Feature Showcase Sections */}
        <div className="w-full max-w-7xl mx-auto px-6 md:px-12 pb-32">
          {/* Feature: Artstream */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="mb-24 relative rounded-3xl overflow-hidden bg-[#1E1E1E] border border-white/5"
          >
            {/* Content */}
            <div className="relative z-10 h-full grid grid-cols-1 md:grid-cols-2 items-center gap-8 px-4 sm:px-8 md:px-16 py-12 md:py-16">
              {/* Left: Text */}
              <div className="text-white text-center md:text-left">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Artstream</h2>
                <p className="text-base sm:text-lg text-gray-300 mb-6">
                  Share your creative work in a masonry feed designed for artists. Get discovered through genuine engagement.
                </p>
                <div className="flex flex-col gap-3 items-center md:items-start">
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                    <span>Chronological & Randomized Feed</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                    <span>Community Discovery</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                    <span>Genuine Engagement</span>
                  </div>
                </div>
              </div>
              
              {/* Right: Visual Mockup */}
              <div className="flex items-center justify-center">
                <div className="relative h-56 w-56 sm:h-64 sm:w-64">
                  {/* Mock feed cards */}
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20, rotate: -5 }}
                      whileInView={{ opacity: 1, x: 0, rotate: i === 1 ? 0 : i * -3 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.2 }}
                      className="absolute bg-[#2A2A2A] border border-white/10 rounded-2xl shadow-2xl p-3 sm:p-4 w-40 sm:w-48"
                      style={{ 
                        top: `${i * 30}px`, 
                        left: `${i * 20}px`,
                        zIndex: 3 - i 
                      }}
                    >
                      <div className="w-full h-32 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-xl mb-3" />
                      <div className="h-2 bg-white/10 rounded w-3/4 mb-2" />
                      <div className="h-2 bg-white/10 rounded w-1/2" />
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Feature: Context-Aware Communities */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="mb-24 relative rounded-3xl overflow-hidden bg-[#1E1E1E] border border-white/5"
          >
            {/* Content */}
            <div className="relative z-10 h-full grid grid-cols-1 md:grid-cols-2 items-center gap-8 px-4 sm:px-8 md:px-16 py-12 md:py-16">
              {/* Left: Visual Mockup */}
              <div className="flex items-center justify-center">
                <div className="relative w-56 h-56 sm:w-64 sm:h-64">
                  {/* Central post card */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-[#2A2A2A] border border-white/10 rounded-2xl shadow-2xl p-3 sm:p-4 w-40 sm:w-48 z-10">
                      <div className="w-full h-24 bg-gradient-to-br from-blue-500/30 to-cyan-500/30 rounded-xl mb-2" />
                      <div className="h-2 bg-white/10 rounded w-full mb-1" />
                      <div className="h-2 bg-white/10 rounded w-2/3" />
                    </div>
                  </div>
                  
                  {/* Floating hashtags around it */}
                  {['#Art', '#Digital', '#3D', '#Photo', '#Design'].map((tag, i) => {
                    const angle = (i * 360) / 5;
                    const radius = 100; // Reduced for mobile compatibility
                    const x = Math.cos((angle * Math.PI) / 180) * radius;
                    const y = Math.sin((angle * Math.PI) / 180) * radius;
                    
                    return (
                      <motion.div
                        key={tag}
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5 + i * 0.1 }}
                        animate={{
                          y: [y, y - 10, y],
                          transition: { repeat: Infinity, duration: 2, delay: i * 0.3 }
                        }}
                        className="absolute bg-[#2A2A2A] border border-cyan-400/30 text-cyan-400 px-3 py-1 rounded-full text-xs font-medium shadow-lg"
                        style={{
                          left: `calc(50% + ${x}px)`,
                          top: `calc(50% + ${y}px)`,
                          transform: 'translate(-50%, -50%)'
                        }}
                      >
                        {tag}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
              
              {/* Right: Text */}
              <div className="text-white text-center md:text-left">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent leading-tight">Communities</h2>
                <p className="text-base sm:text-lg text-gray-300 mb-6">
                  Join communities that match your creative interests. Posts are automatically tagged with context-aware topics.
                </p>
                <div className="flex flex-col gap-3 items-center md:items-start">
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full" />
                    <span>Auto-tagging System</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full" />
                    <span>Topic-based Discovery</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full" />
                    <span>Community Connections</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Feature: Moodboards */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="mb-24 relative rounded-3xl overflow-hidden bg-[#1E1E1E] border border-white/5"
          >
            {/* Content */}
            <div className="relative z-10 h-full grid grid-cols-1 md:grid-cols-2 items-center gap-8 px-4 sm:px-8 md:px-16 py-12 md:py-16">
              {/* Left: Text */}
              <div className="text-white text-center md:text-left">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent leading-tight">Moodboards</h2>
                <p className="text-base sm:text-lg text-gray-300 mb-6">
                  Curate and organize your favorite artwork into collections. Create inspiration boards and share your taste.
                </p>
                <div className="flex flex-col gap-3 items-center md:items-start">
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-pink-400 rounded-full" />
                    <span>Save & Organize</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-pink-400 rounded-full" />
                    <span>Private or Public</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-pink-400 rounded-full" />
                    <span>Share Collections</span>
                  </div>
                </div>
              </div>
              
              {/* Right: Visual Mockup */}
              <div className="flex items-center justify-center">
                <div className="grid grid-cols-2 gap-2 sm:gap-3 w-56 sm:w-64">
                  {[0, 1, 2, 3].map((i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0, rotate: -10 }}
                      whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1, type: "spring" }}
                      whileHover={{ scale: 1.05, rotate: 2 }}
                      className="aspect-square bg-[#2A2A2A] border border-white/10 rounded-2xl shadow-2xl p-2"
                    >
                      <div className={`w-full h-full rounded-xl bg-gradient-to-br ${
                        i === 0 ? 'from-pink-300 to-purple-300' :
                        i === 1 ? 'from-blue-300 to-cyan-300' :
                        i === 2 ? 'from-orange-300 to-amber-300' :
                        'from-green-300 to-emerald-300'
                      }`} />
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Feature: Canvas */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="mb-24 relative rounded-3xl overflow-hidden bg-[#1E1E1E] border border-white/5"
          >
            {/* Content */}
            <div className="relative z-10 h-full grid grid-cols-1 md:grid-cols-2 items-center gap-8 px-4 sm:px-8 md:px-16 py-12 md:py-16">
              {/* Left: Visual Mockup */}
              <div className="flex items-center justify-center mb-8 md:mb-0 md:pr-8">
                <div className="relative w-64 h-64 sm:w-72 sm:h-72">
                  {/* Canvas background with visible grid */}
                  <div className="absolute inset-0 bg-[#1A1A1B] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
                    <svg className="w-full h-full opacity-30">
                      <defs>
                        <pattern id="canvas-grid" width="30" height="30" patternUnits="userSpaceOnUse">
                          <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#444" strokeWidth="1"/>
                        </pattern>
                      </defs>
                      <rect width="100%" height="100%" fill="url(#canvas-grid)" />
                    </svg>
                  </div>
                  
                  {/* Scattered image previews - smaller */}
                  {[
                    { x: 30, y: 40, width: 60, height: 50, rotation: -5, gradient: 'from-purple-400 to-pink-400' },
                    { x: 140, y: 30, width: 55, height: 55, rotation: 8, gradient: 'from-blue-400 to-cyan-400' },
                    { x: 40, y: 150, width: 70, height: 45, rotation: -3, gradient: 'from-orange-400 to-amber-400' },
                    { x: 160, y: 140, width: 50, height: 60, rotation: 5, gradient: 'from-green-400 to-emerald-400' },
                    { x: 100, y: 100, width: 50, height: 50, rotation: -8, gradient: 'from-pink-400 to-rose-400' }
                  ].map((img, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0, rotate: 0 }}
                      whileInView={{ opacity: 1, scale: 1, rotate: img.rotation }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1, type: "spring" }}
                      className={`absolute bg-gradient-to-br ${img.gradient} rounded-lg shadow-xl border-2 border-white/20`}
                      style={{
                        left: `${img.x}px`,
                        top: `${img.y}px`,
                        width: `${img.width}px`,
                        height: `${img.height}px`,
                        zIndex: i
                      }}
                    />
                  ))}
                  
                  {/* Dragging hand cursor */}
                  <motion.div
                    initial={{ opacity: 0, x: -20, y: -20 }}
                    whileInView={{ opacity: 1, x: 0, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 1, duration: 0.5 }}
                    animate={{
                      x: [0, 10, 0],
                      y: [0, 8, 0],
                      transition: { repeat: Infinity, duration: 2, ease: "easeInOut" }
                    }}
                    className="absolute pointer-events-none"
                    style={{ left: '90px', top: '90px', zIndex: 20 }}
                  >
                    <div className="text-4xl drop-shadow-lg">👆</div>
                  </motion.div>
                </div>
              </div>
              
              {/* Right: Text */}
              <div className="text-white text-center md:text-left">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-4 justify-center md:justify-start">
                  <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent leading-tight">Infinite Canvas</h2>
                  <span className="px-4 py-1.5 bg-orange-500/20 text-orange-400 text-sm font-medium rounded-full border border-orange-500/30 whitespace-nowrap w-fit">Coming Soon</span>
                </div>
                <p className="text-base sm:text-lg text-gray-300 mb-6">
                  A canvas with discovery features. Post images and find others' work scattered across an infinite world.
                </p>
                <div className="flex flex-col gap-3 items-center md:items-start">
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-orange-400 rounded-full" />
                    <span>Explore Infinite Space</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-orange-400 rounded-full" />
                    <span>Discover Hidden Work</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-orange-400 rounded-full" />
                    <span>Place Your Art</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Feature: Drops (Coming Soon) */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="mb-24 relative rounded-3xl overflow-hidden bg-[#1E1E1E] border border-white/5"
          >
            {/* Content */}
            <div className="relative z-10 h-full grid grid-cols-1 md:grid-cols-2 items-center gap-8 px-4 sm:px-8 md:px-16 py-12 md:py-16">
              {/* Left: Text */}
              <div className="text-white text-center md:text-left">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-4 justify-center md:justify-start">
                  <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent leading-tight">Drops</h2>
                  <span className="px-4 py-1.5 bg-emerald-500/20 text-emerald-400 text-sm font-medium rounded-full border border-emerald-500/30 whitespace-nowrap w-fit">Coming Soon</span>
                </div>
                <p className="text-base sm:text-lg text-gray-300 mb-6">
                  Reinventing ads with branded digital collectibles hidden in your feed. Find them, collect them, and earn real money instantly.
                </p>
                <div className="flex flex-col gap-3 items-center md:items-start">
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                    <span>Find Hidden Collectibles</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                    <span>Ads That Reward You</span>
                  </div>
                </div>
              </div>
              
              {/* Right: Visual Mockup */}
              <div className="flex items-center justify-center">
                <div className="relative">
                  {/* Main drop card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="bg-[#2A2A2A] border border-white/10 rounded-2xl shadow-2xl p-6 w-56"
                  >
                    <div className="w-full h-40 bg-gradient-to-br from-emerald-500/30 to-teal-500/30 rounded-xl mb-4" />
                    <div className="flex items-center justify-between mb-3">
                      <div className="h-3 bg-white/10 rounded w-20" />
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                        <div className="h-3 bg-emerald-400/30 rounded w-10" />
                      </div>
                    </div>
                    <div className="h-2 bg-white/10 rounded w-full mb-2" />
                    <div className="h-2 bg-white/10 rounded w-3/4" />
                  </motion.div>
                  
                  {/* Floating rarity and price tags */}
                  {[
                    { x: -20, y: -10, text: 'Rare', color: 'bg-purple-500' },
                    { x: 200, y: 60, text: '$5', color: 'bg-emerald-500' }
                  ].map((tag, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.6 + i * 0.2, type: "spring" }}
                      animate={{
                        y: [0, -5, 0],
                        transition: { repeat: Infinity, duration: 2, delay: i * 0.5 }
                      }}
                      className={`absolute ${tag.color} text-white px-3 py-2 rounded-lg text-sm font-bold shadow-lg`}
                      style={{ left: `${tag.x}px`, top: `${tag.y}px` }}
                    >
                      {tag.text}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Fixed Login CTA - offset by sidebar to match main container */}
        <div className="fixed bottom-0 left-0 right-0 z-50 ml-0 md:ml-64">
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1B] via-[#1A1A1B] to-transparent pointer-events-none" 
               style={{ height: '400px', bottom: 0 }} />
          
          {/* Login content */}
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="relative w-full max-w-3xl mx-auto px-6 md:px-12 pb-24 pt-12 text-center"
          >
            <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
              Login to start posting your artwork
            </h2>
            <p className="text-base text-gray-300 mb-5">
              and discover like minded communities
            </p>
            
            <div className="flex justify-center">
              <ConnectButton />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default HomeLanding;
